import { create } from 'zustand';
import { resolveErrorKey } from '@/infrastructure/util/error';
import { PaymentService } from '@/application/service/PaymentService';
import { PaymentApiAdapter } from '@/infrastructure/api/PaymentApiAdapter';
import type { PaymentMethod } from '@/domain/model/PaymentMethod';
import type { PaymentIntent } from '@/domain/model/PaymentIntent';
import type { PaymentTransaction } from '@/domain/model/PaymentTransaction';
import type { PaymentProviderId } from '@/domain/model/PaymentProviderId';
import type { CreatePaymentIntentParams } from '@/domain/port/PaymentRepository';

type PaymentStep = 'method-selection' | 'payment-form' | 'processing' | 'success' | 'error';

interface PaymentState {
  availableMethods: PaymentMethod[];
  currentIntent: PaymentIntent | null;
  paymentHistory: PaymentTransaction[];
  step: PaymentStep;
  selectedProvider: PaymentProviderId | null;
  isLoading: boolean;
  error: string | null;
}

interface PaymentActions {
  fetchAvailableMethods: () => Promise<void>;
  createIntent: (params: CreatePaymentIntentParams) => Promise<PaymentIntent>;
  confirmPayment: (externalPaymentId: string) => Promise<void>;
  fetchPaymentHistory: () => Promise<void>;
  setStep: (step: PaymentStep) => void;
  setSelectedProvider: (provider: PaymentProviderId | null) => void;
  clearIntent: () => void;
  clearError: () => void;
  reset: () => void;
}

const paymentService = new PaymentService(new PaymentApiAdapter());

const initialState: PaymentState = {
  availableMethods: [],
  currentIntent: null,
  paymentHistory: [],
  step: 'method-selection',
  selectedProvider: null,
  isLoading: false,
  error: null,
};

export const usePaymentStore = create<PaymentState & PaymentActions>((set) => ({
  ...initialState,

  fetchAvailableMethods: async () => {
    set({ isLoading: true, error: null });
    try {
      const methods = await paymentService.getAvailableMethods();
      set({ availableMethods: methods, isLoading: false });
    } catch (err) {
      set({ error: resolveErrorKey(err, 'payment.methodsFailed'), isLoading: false });
    }
  },

  createIntent: async (params) => {
    set({ isLoading: true, error: null, step: 'processing' });
    try {
      const intent = await paymentService.createPaymentIntent(params);
      set({ currentIntent: intent, isLoading: false });
      return intent;
    } catch (err) {
      set({ error: resolveErrorKey(err, 'payment.genericError'), isLoading: false, step: 'error' });
      throw err;
    }
  },

  confirmPayment: async (externalPaymentId) => {
    set({ isLoading: true, error: null });
    try {
      await paymentService.confirmPayment(externalPaymentId);
      set({ isLoading: false, step: 'success' });
    } catch (err) {
      set({
        error: resolveErrorKey(err, 'payment.confirmationFailed'),
        isLoading: false,
        step: 'error',
      });
    }
  },

  fetchPaymentHistory: async () => {
    set({ isLoading: true, error: null });
    try {
      const history = await paymentService.getPaymentHistory();
      set({ paymentHistory: history, isLoading: false });
    } catch (err) {
      set({ error: resolveErrorKey(err, 'payment.historyFailed'), isLoading: false });
    }
  },

  setStep: (step) => set({ step }),
  setSelectedProvider: (provider) => set({ selectedProvider: provider }),
  clearIntent: () => set({ currentIntent: null }),
  clearError: () => set({ error: null }),
  reset: () => set(initialState),
}));
